import React, { useEffect, useState, useCallback } from "react";
import ecoisAPI from "../../../api/ecois";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  createTheme,
  Grid,
  IconButton,
  Tab,
  Tabs,
  ThemeProvider,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  InfoOutlined,
  Add,
  Refresh,
  SelectAll,
  Sort,
  ViewList,
  ViewModule,
  Warning,
  ExpandMore,
  Settings,
  BarChart,
  Link as Project,
  MoreVert,
  ChevronRight,
} from "@mui/icons-material";
import useSort from "../../../utils/useSort";
import base64ToImg from "../../../utils/base64ToImg";
import noImage from "../../../assets/noImage.json";
import DeviceDescription from "../../components/deviceDescription";
import SwalMedia from "../../components/swalMedia";
import toFlattenedObj from "../../../utils/toFlattenedObj";
import biosenseAPI from "../../../api/biosense";
import { useTranslation } from "react-i18next";
import getObjectSeriesAbbrBySn from "../../../utils/getObjectSeriesAbbrBySn";
import cardOrListTheme from "../../../assets/cardOrListTheme";

const phoneTheme = cardOrListTheme;

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1350,
      lg: 1880,
      xl: 2410,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
  },
});

export default function DevicesScreen({
  state,
  setState,
  setProjectState,
  historyState,
  setHistoryState,
  topBarState,
  setDeviceDetailsState,
}) {
  const { t } = useTranslation();
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const alertDevices = topBarState.alerts.map((alert) => alert?.device_id);
  const alertTexts = topBarState.alerts.map((alert) => alert?.alert_text);
  const [deviceDesc, setDeviceDesc] = useState(null);
  const [sensorLatest, setSensorLatest] = useState(null);
  const [expandIdx, setExpandIdx] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [sortedDevices, handleSort, SortFieldIcon] = useSort(devices, "alias");

  // 优化：使用useCallback缓存函数，避免重复创建
  const handleProjectButtonOnClick = useCallback(
    (device) => {
      setProjectState({
        selectedProject: {alias: device.project_alias ?? device.alias, id: device.project_id},
      });
      setState({
        pageTabStack: [...state.pageTabStack, state.pageTab],
        pageTab: "projects",
        pageIdxStack: [...state.pageIdxStack, state.pageIdx],
        pageIdx: state.pageIdx + 1,
      });
    },
    [state, setState, setProjectState],
  );

  const handleHistoryButtonOnClick = useCallback(
    (device, deviceTypes) => {
      setState({
        pageTabStack: [...state.pageTabStack, state.pageTab],
        pageTab: "history",
        pageIdxStack: [...state.pageIdxStack, state.pageIdx],
        pageIdx: 1,
      });
      setHistoryState({
        project: device.project_alias ?? device.alias,
        history: {
          ...historyState.history,
          seriesAbbr: device.seriesAbbr,
          sn: device.sn,
          availableTypes: deviceTypes,
          latest: sensorLatest?.datetime,
        },
      });
    },
    [state, setState, setHistoryState, historyState, sensorLatest],
  );

  const handleDeviceDetailsOnClick = useCallback(
    (device) => {
      setState({
        pageTabStack: [...state.pageTabStack, state.pageTab],
        pageTab: "deviceDetails",
        pageIdxStack: [...state.pageIdxStack, state.pageIdx],
        pageIdx: 1,
      });
      setDeviceDetailsState({
        project: device.project_alias ?? device.alias,
        device: device,
      });
    },
    [state, setState, setDeviceDetailsState],
  );

  const handleTabChange = useCallback((e, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleRefresh = useCallback(() => {
    setLastUpdated(new Date());
  }, []);

  const handleAddClick = useCallback(() => {
    console.log("Add button clicked");
  }, []);

  const handleSelectClick = useCallback(() => {
    console.log("Select button clicked");
  }, []);

  // 优化：使用useEffect依赖项数组，避免不必要的重新渲染
  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await biosenseAPI.getDeviceList();
        setDevices(toFlattenedObj(res.devices));
      } catch (err) {
        setError("Failed to load devices");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDevices();
  }, [lastUpdated]);

  useEffect(() => {
    if (expandIdx === null) return;

    setDeviceDesc(null);
    setSensorLatest(null);

    const objectName = getObjectSeriesAbbrBySn(devices, expandIdx);
    const fetchDeviceData = async () => {
      try {
        if (objectName === "esp32s3") {
          const [descRes, valueRes] = await Promise.all([
            biosenseAPI.getDeviceDescription({ sn: expandIdx }),
            biosenseAPI.getDeviceValueListLatest({ sn: expandIdx }),
          ]);
          setDeviceDesc(descRes);
          setSensorLatest(valueRes);
        } else {
          biosenseAPI.getDeviceMode({ sn: expandIdx }).then((modeRes) => {
            ecoisAPI.getDeviceDescription({ sn: expandIdx }).then((res) => {
              const mode = modeRes.mode;
              if (mode === null) {
                setDeviceDesc(res);
              } else {
                const parameters = res.parameters[0];
                if (mode === "soilwater" && parameters.waterLevel) {
                  delete parameters.waterLevel;
                } else if (mode === "waterlevel" && parameters.soilwater) {
                  delete parameters.soilwater;
                }
                setDeviceDesc(res);
              }
            });
          });
          ecoisAPI.getSensorValueListLatest({ sn: expandIdx }).then((res) => setSensorLatest(res));
        }
      } catch (err) {
        setError("Failed to load device details");
        console.error(err);
      }
    };

    fetchDeviceData();
  }, [expandIdx, devices]);

  // 工具栏组件
  const DevicesToolBar = () => {
    // 响应式断点
    const isXSmall = useMediaQuery(theme.breakpoints.down("xs"));
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const isMedium = useMediaQuery(theme.breakpoints.down("md"));

    // 溢出菜单状态
    const [anchorEl, setAnchorEl] = useState(null);
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const openOverflow = Boolean(anchorEl);
    const openSort = Boolean(sortAnchorEl);

    // 排序选项
    const sortOptions = [
      { field: "alias", text: t("name") },
      { field: "sn", text: t("serialnumber") },
      { field: "id", text: t("relatedproject") },
    ];

    // 处理排序菜单打开
    const handleSortClick = useCallback((event) => {
      setSortAnchorEl(event.currentTarget);
    }, []);

    // 处理排序选择
    const handleSortSelect = useCallback(
      (field) => {
        handleSort(field);
        setSortAnchorEl(null);
      },
      [handleSort],
    );

    // 处理溢出菜单打开
    const handleOverflowClick = useCallback((event) => {
      setAnchorEl(event.currentTarget);
    }, []);

    // 处理溢出菜单关闭
    const handleOverflowClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    // 渲染排序菜单
    const renderSortMenu = () => (
      <Menu
        anchorEl={sortAnchorEl}
        open={openSort}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {sortOptions.map((item, idx) => (
          <MenuItem key={idx} onClick={() => handleSortSelect(item.field)} sx={{ minWidth: 180 }}>
            <IconButton
              style={{
                flexDirection: "row",
                alignItems: "center",
                borderRadius: "4px",
                padding: "8px",
                gap: 1,
                mr: 2,
              }}
              size='small'
            >
              <SortFieldIcon field={item.field} />
            </IconButton>
            <Typography variant='body1'>{item.text}</Typography>
          </MenuItem>
        ))}
      </Menu>
    );

    // 渲染溢出菜单
    const renderOverflowMenu = () => (
      <Menu
        anchorEl={anchorEl}
        open={openOverflow}
        onClose={handleOverflowClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleRefresh}>
          <Refresh fontSize='small' sx={{ mr: 2 }} />
          <Typography variant='body1'>{t("refresh")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleAddClick}>
          <Add fontSize='small' sx={{ mr: 2 }} />
          <Typography variant='body1'>{t("add")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleSelectClick}>
          <SelectAll fontSize='small' sx={{ mr: 2 }} />
          <Typography variant='body1'>{t("select")}</Typography>
        </MenuItem>
      </Menu>
    );

    // 渲染工具栏
    return (
      <AppBar position='static' sx={{ backgroundColor: "white", boxShadow: 4 }}>
        <Toolbar
          sx={{
            columnGap: 1,
            flexWrap: "wrap",
            paddingLeft: isXSmall ? 1 : 2,
            paddingRight: isXSmall ? 1 : 2,
          }}
        >
          {/* 视图切换标签 */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label='data display tabs'
            variant={isXSmall ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTabs-flexContainer": {
                flexWrap: "nowrap",
              },
              mr: isMedium ? 1 : 2,
            }}
          >
            <Tab
              icon={<ViewModule />}
              label={isXSmall ? null : t("list.card")}
              aria-label='Cards'
              sx={{
                flexDirection: isXSmall ? "column" : "row",
                alignItems: "center",
                gap: 1,
                minWidth: isXSmall ? "auto" : undefined,
              }}
            />
            <Tab
              icon={<ViewList />}
              label={isXSmall ? null : t("list.list")}
              aria-label='List'
              sx={{
                flexDirection: isXSmall ? "column" : "row",
                alignItems: "center",
                gap: 1,
                minWidth: isXSmall ? "auto" : undefined,
              }}
            />
          </Tabs>

          {/* 排序部分 */}
          {!isSmall && (
            <Box display='flex' alignItems='center' mr={isMedium ? 1 : 2}>
              <Typography variant='body1' color='grey' mr={1}>
                {t("list.sort")}:
              </Typography>

              {isMedium ? (
                <IconButton
                  onClick={handleSortClick}
                  size='small'
                  aria-controls='sort-menu'
                  aria-haspopup='true'
                >
                  <Sort />
                  <Typography variant='body1' ml={1}>
                    {sortOptions.find((opt) => opt.field === handleSort.currentField)?.text ||
                      t("name")}
                  </Typography>
                  <ChevronRight fontSize='small' />
                </IconButton>
              ) : (
                <>
                  {sortOptions.map((item, idx) => (
                    <IconButton
                      key={idx}
                      onClick={() => handleSort(item.field)}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderRadius: "4px",
                        padding: "8px",
                        gap: 1,
                      }}
                      size='small'
                    >
                      <SortFieldIcon field={item.field} />
                      <Typography variant='body1'>{item.text}</Typography>
                    </IconButton>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* 右侧操作按钮 */}
          <Box sx={{ flexGrow: 1 }} />

          {!isMedium && (
            <>
              <Typography variant='body' color='grey' mr={2}>
                {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
              </Typography>

              <Tooltip title={t("refresh")}>
                <IconButton aria-label='refresh' onClick={handleRefresh} size='small'>
                  <Refresh />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("add")}>
                <IconButton aria-label='add' onClick={handleAddClick} size='small'>
                  <Add />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("select")}>
                <IconButton aria-label='select' edge='end' onClick={handleSelectClick} size='small'>
                  <SelectAll />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* 溢出菜单 - 小屏幕时显示 */}
          {isMedium && (
            <>
              <Typography
                variant='body'
                color='grey'
                mr={2}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
              </Typography>

              <IconButton
                aria-label='more'
                aria-controls='overflow-menu'
                aria-haspopup='true'
                onClick={handleOverflowClick}
                size='small'
              >
                <MoreVert />
              </IconButton>
            </>
          )}
        </Toolbar>

        {/* 第二行 - 仅在超小屏幕显示 */}
        {isXSmall && (
          <Toolbar
            variant='dense'
            sx={{
              paddingTop: 0,
              paddingLeft: isXSmall ? 1 : 2,
              paddingRight: isXSmall ? 1 : 2,
            }}
          >
            <Typography variant='body2' color='grey' sx={{ flexGrow: 1 }}>
              {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
            </Typography>

            {!isSmall && (
              <IconButton
                aria-label='sort'
                aria-controls='sort-menu'
                aria-haspopup='true'
                onClick={handleSortClick}
                size='small'
              >
                <Sort />
              </IconButton>
            )}
          </Toolbar>
        )}

        {renderSortMenu()}
        {renderOverflowMenu()}
      </AppBar>
    );
  };
  // 加载状态显示
  if (loading) {
    return (
      <Box display={"flex"} flexDirection={"column"} rowGap={3} height={"100%"}>
        <DevicesToolBar />
        <Box flexGrow={1} display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress sx={{ mt: 1 }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display={"flex"} flexDirection={"column"} rowGap={3} height={"100%"}>
        <DevicesToolBar />
        <Box flexGrow={1} display='flex' justifyContent='center' alignItems='center'>
          <Alert severity='error'>{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box display={"flex"} flexDirection={"column"} rowGap={3}>
      <DevicesToolBar />
      {tabValue ? (
        <List
          sx={{
            backgroundColor: "white",
            p: 1.5,
            borderRadius: "4px",
            boxShadow: 4,
          }}
        >
          {sortedDevices.map((device, idx) => (
            <DeviceListItem
              key={`device-list-${idx}`}
              device={device}
              expandIdx={expandIdx}
              setExpandIdx={setExpandIdx}
              handleHistoryButtonOnClick={handleHistoryButtonOnClick}
              handleDeviceDetailsButtonOnClick={handleDeviceDetailsOnClick}
              handleProjectButtonOnClick={handleProjectButtonOnClick}
              deviceDesc={deviceDesc}
              sensorLatest={sensorLatest}
              img={device.img ?? noImage.img}
              t={t}
              alertDevices={alertDevices}
              alertTexts={alertTexts}
            />
          ))}
        </List>
      ) : (
        <ThemeProvider theme={theme}>
          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12, lg: 12, xl: 12 }}>
            {sortedDevices.map((device, idx) => (
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                xl={3}
                key={device.sn}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <DeviceCard
                  key={`site-card-${idx}`}
                  device={device}
                  expandIdx={expandIdx}
                  setExpandIdx={setExpandIdx}
                  handleHistoryButtonOnClick={handleHistoryButtonOnClick}
                  handleDeviceDetailsButtonOnClick={handleDeviceDetailsOnClick}
                  handleProjectButtonOnClick={handleProjectButtonOnClick}
                  deviceDesc={deviceDesc}
                  sensorLatest={sensorLatest}
                  img={device.img ?? noImage.img}
                  t={t}
                  alertDevices={alertDevices}
                  alertTexts={alertTexts}
                />
              </Grid>
            ))}
          </Grid>
        </ThemeProvider>
      )}
    </Box>
  );
}

const DeviceCard = ({
  device,
  expandIdx,
  setExpandIdx,
  handleHistoryButtonOnClick,
  deviceDesc,
  sensorLatest,
  img,
  handleProjectButtonOnClick,
  handleDeviceDetailsButtonOnClick,
  t,
  alertDevices,
  alertTexts,
}) => {
  const isPhone = useMediaQuery(phoneTheme.breakpoints.down("sm"));
  const deviceId = device.id;
  const isAlertDevice = alertDevices.includes(deviceId);
  const alertIndex = isAlertDevice ? alertDevices.indexOf(deviceId) : -1;
  const alertText = isAlertDevice ? alertTexts[alertIndex] : "";
  const isCurrentExpanded = expandIdx === device.sn;
  const deviceTypes = Object.keys(deviceDesc?.parameters?.[0] || {});

  // 渲染设备基本信息表格
  const renderDeviceInfoTable = () => (
    <Table sx={{ borderCollapse: "collapse" }}>
      <TableBody>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("serialnumber")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.sn}
          </TableCell>
        </TableRow>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("name")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.alias ?? "(No Name)"}
          </TableCell>
        </TableRow>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("series")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.series} - {device.type}
          </TableCell>
        </TableRow>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("relatedproject")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.project_alias ?? "(No Project)"}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  // 渲染移动设备上的图标按钮
  const renderPhoneButtons = () => (
    <Grid container spacing={1} justifyContent='center' mt={1}>
      <Grid item>
        <Tooltip title={t("gotoproject")}>
          <IconButton
            size='small'
            color='warning'
            onClick={handleProjectButtonOnClick}
            disabled={device.project_id === -1}
          >
            <Project fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 9, textAlign: "center" }}>
          {t("gotoproject")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("list.details")}>
          <IconButton
            size='small'
            color='warning'
            onClick={() => handleDeviceDetailsButtonOnClick(device)}
          >
            <Settings fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 9, textAlign: "center" }}>
          {t("list.details")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={isCurrentExpanded ? t("list.close") : t("recentdata")}>
          <IconButton
            size='small'
            color={isCurrentExpanded ? "warning" : "success"}
            onClick={() => (isCurrentExpanded ? setExpandIdx(null) : setExpandIdx(device.sn))}
          >
            <BarChart fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 9, textAlign: "center" }}>
          {isCurrentExpanded ? t("list.close") : t("recentdata")}
        </Typography>
      </Grid>
    </Grid>
  );

  // 渲染桌面设备上的普通按钮
  const renderDesktopButtons = () => (
    <Box display='flex' flexDirection='column' justifyContent='center' width='8em' gap={1.5}>
      <ProjectButton device={device} handleButtonOnClick={() => handleProjectButtonOnClick(device)} t={t} />
      <DeviceButton handleButtonOnClick={() => handleDeviceDetailsButtonOnClick(device)} t={t} />
      <ExpandButton
        isCurrentExpanded={isCurrentExpanded}
        setExpandIdx={setExpandIdx}
        sn={device.sn}
        t={t}
      />
    </Box>
  );

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      sx={{
        backgroundColor: "white",
        p: 1.5,
        borderRadius: "4px",
        boxShadow: 4,
        borderBottom: isAlertDevice ? "6px solid #FF9966" : "none",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <SwalMedia
          component='img'
          isWithBlur={false}
          title={device.alias ?? "(No Name)"}
          height='150'
          width='150'
          style={{
            objectFit: "cover",
            objectPosition: "0 -50px",
            borderRadius: "4px 4px 0 0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
          src={base64ToImg({ base64String: img })}
          alt='Card Background'
        />

        <Box display='flex' flexDirection='column' width='calc(100% - 100px)' /* 调整宽度 */>
          <Box display='flex' flexDirection='row' justifyContent='space-between'>
            <Box>
              {renderDeviceInfoTable()}

              {isAlertDevice ? (
                <Box display='flex' alignItems='center' mt={1}>
                  <Chip
                    sx={{
                      p: 0.25,
                      backgroundColor: "#ff996688",
                      color: "black",
                      zIndex: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: 10,
                    }}
                    label={alertText}
                    icon={<Warning color='white' />}
                  />
                  <Tooltip title={alertText} sx={{ ml: 1 }}>
                    <IconButton size='small' color='inherit'>
                      <InfoOutlined />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : null}
            </Box>

            {!isPhone && renderDesktopButtons()}
          </Box>
        </Box>
      </Box>

      {/* 按钮在移动设备上显示在下方 */}
      {isPhone && renderPhoneButtons()}

      <DeviceDescription
        device={device}
        expandIdx={expandIdx}
        deviceDesc={deviceDesc}
        sensorLatest={sensorLatest}
        historyButtonOnClick={() => handleHistoryButtonOnClick(device, deviceTypes)}
      />
    </Box>
  );
};

const DeviceListItem = ({
  device,
  expandIdx,
  setExpandIdx,
  handleHistoryButtonOnClick,
  deviceDesc,
  sensorLatest,
  img,
  handleProjectButtonOnClick,
  handleDeviceDetailsButtonOnClick,
  t,
  alertDevices,
  alertTexts,
}) => {
  const isPhone = useMediaQuery(phoneTheme.breakpoints.down("sm"));
  const deviceId = device.id;
  const isAlertDevice = alertDevices.includes(deviceId);
  const alertIndex = isAlertDevice ? alertDevices.indexOf(deviceId) : -1;
  const alertText = isAlertDevice ? alertTexts[alertIndex] : "";
  const listTitle = `${device.series} - ${device.type} - ${device.alias ?? "(No Name)"}`;
  const isCurrentExpanded = expandIdx === device.sn;
  const deviceTypes = Object.keys(deviceDesc?.parameters?.[0] || {});

  // 移动设备上的图标按钮
  const renderPhoneButtons = () => (
    <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Tooltip title={t("gotoproject")}>
          <IconButton
            size='small'
            color='warning'
            onClick={handleProjectButtonOnClick}
            disabled={device.project_id === -1}
          >
            <Project fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 10 }}>
          {t("gotoproject")}
        </Typography>
      </Box>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Tooltip title={t("list.details")}>
          <IconButton
            size='small'
            color='warning'
            onClick={() => handleDeviceDetailsButtonOnClick(device)}
          >
            <Settings fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 10 }}>
          {t("list.details")}
        </Typography>
      </Box>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Tooltip title={isCurrentExpanded ? t("list.close") : t("recentdata")}>
          <IconButton
            size='small'
            color={isCurrentExpanded ? "warning" : "success"}
            onClick={() => (isCurrentExpanded ? setExpandIdx(null) : setExpandIdx(device.sn))}
          >
            <BarChart fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 10 }}>
          {isCurrentExpanded ? t("list.close") : t("recentdata")}
        </Typography>
      </Box>
    </Box>
  );

  // 桌面设备上的普通按钮
  const renderDesktopButtons = () => (
    <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
      <ProjectButton
        device={device}
        handleButtonOnClick={() => handleProjectButtonOnClick(device)}
        t={t}
        size='small'
      />
      <DeviceButton
        handleButtonOnClick={() => handleDeviceDetailsButtonOnClick(device)}
        t={t}
        size='small'
      />
      <ExpandButton
        isCurrentExpanded={isCurrentExpanded}
        setExpandIdx={setExpandIdx}
        sn={device.sn}
        t={t}
        size='small'
      />
    </Box>
  );

  return (
    <li key={`device-list-item-${device.sn}`}>
      <ListItem
        sx={{
          borderLeft: isAlertDevice ? "6px solid #FF9966" : "none",
          padding: 2,
          "&:last-child": {
            borderBottom: 0,
          },
        }}
      >
        <ListItemAvatar>
          <SwalMedia
            component='Avatar'
            title={device.alias ?? "(No Name)"}
            height='60'
            width='60'
            style={{
              objectFit: "cover",
            }}
            src={base64ToImg({ base64String: img })}
            alt='List Avatar'
          />
        </ListItemAvatar>
        <Box display='flex' flexDirection='column' flexGrow={1}>
          <ListItemText
            sx={{
              flexGrow: 1,
              "& .MuiListItemText-secondary": {
                whiteSpace: "pre-wrap",
                fontSize: 13,
              },
            }}
            primary={
              <Box display='flex' alignItems='center'>
                <Typography variant='body1' fontWeight={500}>
                  {listTitle}
                </Typography>
                {!isPhone && isAlertDevice ? (
                  <Chip
                    sx={{
                      ml: 1,
                      p: 0.25,
                      backgroundColor: "#ff996688",
                      color: "black",
                      zIndex: 1,
                      fontSize: 11,
                    }}
                    label={alertText}
                    icon={<Warning color='black' fontSize='small' />}
                  />
                ) : null}
              </Box>
            }
            secondary={`${t("serialnumber")}: ${device.sn}\n${t("relatedproject")}: ${device.project_alias ?? "(No Project)"}`}
          />
          {isPhone && isAlertDevice ? (
            <Box display='flex' alignItems='center' mt={1}>
              <Chip
                sx={{
                  p: 0.25,
                  backgroundColor: "#ff996688",
                  color: "black",
                  zIndex: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 10,
                }}
                label={alertText}
                icon={<Warning color='white' />}
              />
              <Tooltip title={alertText} sx={{ ml: 1 }}>
                <IconButton size='small' color='inherit'>
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
        {isPhone ? renderPhoneButtons() : renderDesktopButtons()}
      </ListItem>
      <DeviceDescription
        device={device}
        expandIdx={expandIdx}
        deviceDesc={deviceDesc}
        sensorLatest={sensorLatest}
        historyButtonOnClick={() => handleHistoryButtonOnClick(device, deviceTypes)}
      />
    </li>
  );
};

// 优化：拆分为独立的按钮组件
const ExpandButton = ({ isCurrentExpanded, setExpandIdx, sn, t, size = "medium" }) => {
  return (
    <Button
      variant='contained'
      size={size}
      sx={{ borderRadius: "4px", textTransform: "none" }}
      color={isCurrentExpanded ? "warning" : "success"}
      onClick={() => (isCurrentExpanded ? setExpandIdx(null) : setExpandIdx(sn))}
      startIcon={isCurrentExpanded ? <ExpandMore /> : null}
    >
      {isCurrentExpanded ? t("list.close") : t("recentdata")}
    </Button>
  );
};

const DeviceButton = ({ handleButtonOnClick, t, size = "medium" }) => {
  return (
    <Button
      variant='contained'
      size={size}
      sx={{ borderRadius: "4px", textTransform: "none" }}
      color={"warning"}
      onClick={handleButtonOnClick}
    >
      {t("list.details")}
    </Button>
  );
};

const ProjectButton = ({ device, handleButtonOnClick, t, size = "medium" }) => {
  const projectId = device.project_id;
  return (
    <Button
      variant='contained'
      size={size}
      sx={{ borderRadius: "4px", textTransform: "none" }}
      disabled={projectId === -1}
      color={"warning"}
      onClick={handleButtonOnClick}
    >
      {t("gotoproject")}
    </Button>
  );
};
